import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { useTheme } from '@mui/material/styles';
import { Box, Container, GlobalStyles, Link } from '@mui/material';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import enGB from 'date-fns/locale/en-GB';
// components
import Page from 'src/components/Page';
import Icon from 'src/components/universe/Icon';
import { UniverseSkin } from 'src/components/universe/skin';
import PageHeader from 'src/components/universe/shell/PageHeader';
import { useFocusTask } from 'src/layouts/vendor';
import { pickerLayerStyles } from './pickerLayers';
// universe
import { LAYER_FONT_STYLES, PAGE_FONT_SX } from 'src/theme/universe/pageFont';
import { LAYER_SCRIM_STYLES } from 'src/theme/universe/surfaceSx';
import {
  UNIVERSE_FONT,
  accentOf,
  focusRingSx,
  workspaceCanvasOf
} from 'src/theme/universe/brand';

// ---------------------------------------------------------------------------
// THE FRAME OF A VENDOR PORTAL PAGE — every page under /vendors chooses the
// Universe skin through this one component (DESIGN.md §28: a page migrates
// by CHOOSING its skin), so the shared pieces it is made of draw the system
// here and their classic look everywhere else.
//
// It carries what each migrated intranet page carries on its own: the UI
// face on the page and on its layers (menus, popovers, dialogs render in
// portals outside the page), the §7 scrim behind every dialog, the record's
// deeper canvas where the page is a record, the page header (§12: the title,
// one meta line, the one primary action — no breadcrumbs), and a quiet way
// back where the page is reached from another one.
//
//   width   'prose' 960px for a form or settings page · 'page' the `lg`
//           container · 'wide' the `xl` container a list uses
//   canvas  'record' paints the deeper workspace canvas while mounted
//   focus   on a phone, the foot bar steps aside while this page is up — a
//           page that is one task with its own sticky action bar
// ---------------------------------------------------------------------------

function RecordCanvas() {
  const theme = useTheme();
  const canvas = workspaceCanvasOf(theme);
  return <GlobalStyles styles={{ 'main#vendor-main, body': { backgroundColor: canvas } }} />;
}

Back.propTypes = { to: PropTypes.string.isRequired, label: PropTypes.string.isRequired };

export function Back({ to, label }) {
  const theme = useTheme();
  return (
    <Link
      component={RouterLink}
      to={to}
      underline="none"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        mb: 1.5,
        minHeight: 24,
        fontFamily: UNIVERSE_FONT,
        fontSize: 13,
        fontWeight: 500,
        lineHeight: 1.4,
        color: 'text.secondary',
        borderRadius: '4px',
        '&:hover': { color: accentOf(theme) },
        '&:focus-visible': focusRingSx(theme),
        '@media (pointer: coarse)': { minHeight: 44 }
      }}
    >
      <Icon name="ArrowLeft" size="xs" />
      {label}
    </Link>
  );
}

const WIDTHS = { prose: 960, page: 'lg', wide: 'xl' };

VendorPage.propTypes = {
  /** The browser tab's title. */
  document: PropTypes.string.isRequired,
  /** The page title; a record page draws its own identity band instead. */
  title: PropTypes.node,
  meta: PropTypes.node,
  action: PropTypes.node,
  secondaryActions: PropTypes.node,
  back: PropTypes.shape({ to: PropTypes.string, label: PropTypes.string }),
  width: PropTypes.oneOf(['prose', 'page', 'wide']),
  canvas: PropTypes.oneOf(['portal', 'record']),
  focus: PropTypes.bool,
  children: PropTypes.node
};

export default function VendorPage({
  document: documentTitle,
  title,
  meta,
  action,
  secondaryActions,
  back,
  width = 'page',
  canvas = 'portal',
  focus = false,
  children
}) {
  const theme = useTheme();
  useFocusTask(focus);
  const max = WIDTHS[width];
  return (
    <Page title={documentTitle}>
      <UniverseSkin>
        <GlobalStyles styles={LAYER_FONT_STYLES} />
        <GlobalStyles styles={LAYER_SCRIM_STYLES} />
        <GlobalStyles styles={pickerLayerStyles(theme)} />
        {canvas === 'record' && <RecordCanvas />}
        <Container
          maxWidth={typeof max === 'string' ? max : false}
          sx={{
            ...PAGE_FONT_SX,
            minWidth: 0,
            px: { xs: 2, sm: 3 },
            ...(typeof max === 'number' && { maxWidth: max })
          }}
        >
          {back && <Back to={back.to} label={back.label} />}
          {title && (
            <PageHeader
              title={title}
              meta={meta}
              action={action}
              secondaryActions={secondaryActions}
            />
          )}
          {/* the pickers on a vendor page start their weeks on Monday (§16):
              en-GB, here, rather than the app's en-US default */}
          <LocalizationProvider dateAdapter={AdapterDateFns} locale={enGB}>
            <Box sx={{ minWidth: 0 }}>{children}</Box>
          </LocalizationProvider>
        </Container>
      </UniverseSkin>
    </Page>
  );
}
